/* Premium visual modernization — top scroll-progress bar.
 *
 * A thin orange gradient bar pinned to the top of the viewport that
 * fills as the page scrolls. Mirrors the `.progress` element in
 * docs/TSG_MicroAIs_FinancialServices.html. Purely decorative
 * (aria-hidden); tracks window scroll with a rAF-throttled listener so
 * it never thrashes layout. Rendered only on the report screens.
 */

import { useEffect, useState } from 'react';
import type { JSX } from 'react';

export function ScrollProgress(): JSX.Element {
  const [pct, setPct] = useState(0);

  useEffect(() => {
    let raf = 0;

    const compute = (): void => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      const top = window.scrollY || doc.scrollTop || 0;
      setPct(max > 0 ? Math.min(100, (top / max) * 100) : 0);
    };

    const onScroll = (): void => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        compute();
      });
    };

    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="pm-scroll-progress" style={{ width: `${pct}%` }} aria-hidden="true" />
  );
}
